import {
    VpsPressureUnit,
    VpsGravityType,
    VpsTemperatureUnit,
    VpsQuantityUnit,
    VpsCommodity,
    Approximates,
    RoundValueASTM,
    RoundValueASTM_ValAsStr,
    VerifyNumericLimit_Btw_MinMax,
    VerifyNumericLimit_ByLimit,
    RemoveScientificNotation,
    GetDecimalsFromString
} from './FuelMath';
import CTPL from './CTPL';
import FuelCalcBase from './FuelCalcBase';
import { MathFormula } from './Math';
import { ParameterType } from '../../services/bunkermaster/Settings'

const T15C = "15";
const T60F = "60";

export default class QuantityConversion extends FuelCalcBase {

    //#region private members    
    #m_Commodity;
    #m_ObsGravityVl = "";
    #m_ObsGravityType;
    #m_ObsGravityTemp = "";
    #m_ObsGravityTempUnit;
    #m_ObsGravityPress = "";
    #m_ObsGravityPressUnit;

    #m_ObsQuantityVl = "";
    #m_ObsQuantityUnit;
    #m_ObsQtyTemperature = "";
    #m_ObsQtyTemperatureUnit;
    #m_ObsQtyPressure = "";
    #m_ObsQtyPressureUnit;

    #m_AltQuantityUnit;
    #m_AltQtyTemperature = "";
    #m_AltQtyTemperatureUnit;
    #m_AltQtyPressure = "";
    #m_AltQtyPressureUnit;

    #m_BaseVolume;
    #m_BaseGravity;
    #m_BaseDensity;
    #m_AltVolume;
    #m_AltGravity;
    #m_CTPL_ObsToBase;
    #m_CTPL_BaseToAlt;


    #m_M3 = "";
    #m_BBL = "";
    #m_MT = "";
    #m_Mass = "";
    #m_Density15C;
    #m_WCF = "";
    #m_VCF = "";

    //#endregion

    constructor() {
        super();
        this.#m_ObsGravityPress = "0";
        this.#m_ObsGravityPressUnit = VpsPressureUnit.Psi;
        this.#m_ObsQtyPressure = "0";
        this.#m_ObsQtyPressureUnit = VpsPressureUnit.Psi;
        this.#m_AltQtyPressure = "0";
        this.#m_AltQtyPressureUnit = VpsPressureUnit.Psi;

        this.#m_Commodity = null;
        this.#m_ObsGravityType = null;
        this.#m_ObsQuantityUnit = null;
        this.#m_ObsGravityTempUnit = null;
        this.#m_ObsQtyTemperatureUnit = null;
        this.#m_AltQtyTemperatureUnit = null;
    }

    //#region Properties
    get ObservedGravityValue() {
        return this.#m_ObsGravityVl;
    }
    set ObservedGravityValue(value) {

        if (!this.CheckDecimalCharacter(value, "Observed Gravity Value"))
            return;
        this.#m_ObsGravityVl = value;
    }

    get ObservedGravityType() {
        return this.#m_ObsGravityType;
    }
    set ObservedGravityType(value) {

        if (value < VpsGravityType.Density || value > VpsGravityType.API) {
            this.RaiseError(1000, "Observed Gravity Type")
            return;
        }
        this.#m_ObsGravityType = value;
    }

    get ObservedGravityTemperature() {
        return this.#m_ObsGravityTemp;

    }
    set ObservedGravityTemperature(value) {

        if (!this.CheckDecimalCharacter(value, "Observed Gravity Temperature"))
            return;

        this.#m_ObsGravityTemp = value;
    }

    get ObservedGravityTemperatureUnit() {

        return this.#m_ObsGravityTempUnit;
    }
    set ObservedGravityTemperatureUnit(value) {

        if ((value < VpsTemperatureUnit.Celcius) || (value > VpsTemperatureUnit.Fahrenheit)) {
            this.RaiseError(1000, "Observed Gravity Temperature Unit")
            return;
        }
        this.#m_ObsGravityTempUnit = value;
    }

    get ObservedGravityPressure() {
        return this.#m_ObsGravityPress.toString();
    }
    set ObservedGravityPressure(value) {
        if (!this.CheckDecimalCharacter(value))
            return
        else if (value != "")
            this.#m_ObsGravityPress = value;
    }

    get ObservedGravityPressureUnit() {
        return this.#m_ObsGravityPressUnit;
    }
    set ObservedGravityPressureUnit(value) {
        if (value != null && (value < VpsPressureUnit.kPa || value > VpsPressureUnit.Psi)) {
            this.RaiseError(1000, "Observed Pressure Unit");
            return;
        }
        this.#m_ObsGravityPressUnit = value;
    }

    get ObservedQuantityValue() {
        return this.#m_ObsQuantityVl;
    }
    set ObservedQuantityValue(value) {

        let propertyNm = "Observed Quantity Value";
        if (!this.CheckDecimalCharacter(value, propertyNm))
            return
        else if (!VerifyNumericLimit_ByLimit(value, 0, true)) {
            this.RaiseError(1110, propertyNm)
            return;
        }
        this.#m_ObsQuantityVl = value;
    }

    get ObservedQuantityUnit() {
        return this.#m_ObsQuantityUnit;
    }
    set ObservedQuantityUnit(value) {

        if (value < VpsQuantityUnit.CubicMetres || value > VpsQuantityUnit.MetricTons) {
            this.RaiseError(1000, "Observed Quantity Unit")
            return;
        }
        this.#m_ObsQuantityUnit = value;
    }

    get ObservedQuantityTemperature() {
        return this.#m_ObsQtyTemperature;
    }
    set ObservedQuantityTemperature(value) {

        if (!this.CheckDecimalCharacter(value, "Observed Quantity Temperature Value"))
            return;
        this.#m_ObsQtyTemperature = value;
    }

    get ObservedQuantityTemperatureUnit() {
        return this.#m_ObsQtyTemperatureUnit;
    }
    set ObservedQuantityTemperatureUnit(value) {

        if (value != null && (value < VpsTemperatureUnit.Celcius || value > VpsTemperatureUnit.Fahrenheit)) {
            this.RaiseError(1000, "Observed Quantity Temperature Unit")
            return;
        }
        this.#m_ObsQtyTemperatureUnit = value
    }

    get ObservedQuantityPressure() {
        return this.#m_ObsQtyPressure;
    }
    set ObservedQuantityPressure(value) {
        if (!this.CheckDecimalCharacter(value, "Observed Quantity Pressure Value"))
            return
        else if (value != "")
            this.#m_ObsQtyPressure = value
    }

    get ObservedQuantityPressureUnit() {
        return this.#m_ObsQtyPressureUnit
    }
    set ObservedQuantityPressureUnit(value) {
        if (value != null && (value < VpsPressureUnit.kPa || value > VpsPressureUnit.Psi)) {
            this.RaiseError(1000, "Observed Quantity Pressure Unit");
            return;
        }
        this.#m_ObsQtyPressureUnit = value
    }

    get AlternateQuantityUnit() {
        return this.#m_AltQuantityUnit;
    }
    set AlternateQuantityUnit(value) {

        if (value < VpsQuantityUnit.CubicMetres || value > VpsQuantityUnit.MetricTons) {
            this.RaiseError(1000, "Alternate Quantity Unit")
            return;
        }
        this.#m_AltQuantityUnit = value;
    }

    get AlternateQuantityTemperature() {
        return this.#m_AltQtyTemperature;
    }
    set AlternateQuantityTemperature(value) {
        if (!this.CheckDecimalCharacter(value, "Alternate Quantity Temperature Value"))
            return;
        this.#m_AltQtyTemperature = value;
    }

    get AlternateQuantityTemperatureUnit() {
        return this.#m_AltQtyTemperatureUnit;
    }
    set AlternateQuantityTemperatureUnit(value) {
        if (value != null && (value < VpsTemperatureUnit.Celcius || value > VpsTemperatureUnit.Fahrenheit)) {
            this.RaiseError(1000, "Alternate Quantity Temperature Unit")
            return;
        }
        this.#m_AltQtyTemperatureUnit = value;
    }

    get AlternateQuantityPressure() {
        return this.#m_AltQtyPressure;
    }
    set AlternateQuantityPressure(value) {
        if (!this.CheckDecimalCharacter(value, "Alternate Quantity Pressure Value"))
            return;
        else if (value != "")
            this.#m_AltQtyPressure = value;
    }


    get AlternateQuantityPressureUnit() {
        return this.#m_AltQtyPressureUnit;
    }
    set AlternateQuantityPressureUnit(value) {
        if (value != null && (value < VpsPressureUnit.kPa || value > VpsPressureUnit.Psi)) {
            this.RaiseError(1000, "Alternate Quantity Pressure Unit");
            return;
        }
        this.#m_AltQtyPressureUnit = value;
    }


    get Commodity() {
        return this.#m_Commodity;
    }
    set Commodity(value) {

        if (value < VpsCommodity.CrudeOil || value > VpsCommodity.LubricatingOil) {
            this.RaiseError(1005, "Commodity");
            return;
        }
        this.#m_Commodity = value;
    }

    get CubicMetres() {
        return RoundValueASTM_ValAsStr(this.#m_M3, 0.0001);
    }

    get MetricTons() {
        return MathFormula.roundAccurately(this.#m_MT, 4);
    }

    get Barrels() {
        return RoundValueASTM_ValAsStr(this.#m_BBL, 0.0001);
    }

    get US_Gallons() {
        return RoundValueASTM_ValAsStr((parseFloat(this.#m_BBL) * 42).toString(), 0.0001);
    }

    get WeightFactor() {
        return this.#m_WCF;
    }

    get VolumeConversionFactor() {
        return this.#m_VCF;
    }

    get Density15C() {
        if (this.#m_Density15C == 0)
            return "";
        else
            return this.#m_Density15C.toString();
    }

    get Mass_MT() {
        return RoundValueASTM_ValAsStr(this.#m_Mass, 0.001);
    }
    //#endregion

    MinimumDetails() {
        let MinimumDetails = false;
        if (!(this.#m_Commodity == null || this.#m_ObsGravityVl.length == 0 || this.#m_ObsGravityType == null
            || this.#m_ObsGravityTemp.length == 0 || this.#m_ObsGravityTempUnit == null || this.#m_ObsQuantityVl.length == 0 ||
            this.#m_ObsGravityType == null || this.#m_ObsQuantityUnit == null)) {
            MinimumDetails = true;
        }

        if ((this.#m_ObsQtyTemperature.length == 0 || this.#m_ObsQtyTemperatureUnit == null) && (this.#m_ObsQuantityUnit != VpsQuantityUnit.MetricTons)) {
            MinimumDetails = false;
        }

        if ((this.#m_AltQtyTemperature.length == 0 || this.#m_AltQtyTemperatureUnit == null) && (this.#m_AltQuantityUnit != VpsQuantityUnit.MetricTons)) {
            MinimumDetails = false;
        }

        if (MinimumDetails == false) {
            this.RaiseError(1004, "Convert Quantity Method");
        }

        return MinimumDetails;
    }

    // Todo: Need to check. CTPL flow
    GetDensity15C() {
        //
        const methodName = "Get Base Density @ 15C";

        let toBaseCTPL = new CTPL();
        try {

            toBaseCTPL.Commodity = this.#m_Commodity;
            toBaseCTPL.ObservedGravityValue = this.#m_ObsGravityVl;
            toBaseCTPL.ObservedGravityType = this.#m_ObsGravityType;
            toBaseCTPL.ObservedTemperature = this.#m_ObsGravityTemp;
            toBaseCTPL.ObservedTemperatureUnit = this.#m_ObsGravityTempUnit;
            toBaseCTPL.BaseTemperature = T15C;
            toBaseCTPL.BaseTemperatureUnit = VpsTemperatureUnit.Celcius;
            toBaseCTPL.CalculateCTPL();
            if (toBaseCTPL.Exception) {
                this.RaiseError_PropNme(methodName, toBaseCTPL.ExceptionDescription);
                return false;
            }
            else {
                this.#m_Density15C = parseFloat(toBaseCTPL.GetBaseGravity(VpsGravityType.Density));
                this.#m_CTPL_ObsToBase = RoundValueASTM(parseFloat(toBaseCTPL.CTPL_ObsToBase), 0.00001);
                return true;
            }
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }

    // Todo: Need to check. CTPL flow
    GetAlternateValues(baseGravityValue, baseGravityType, baseVolume) {
  //;
        const methodName = "Get Alternate Values Method";

        const toAltCTPL = new CTPL();
        try {
            toAltCTPL.Commodity = this.#m_Commodity;
            toAltCTPL.ObservedGravityValue = baseGravityValue;
            toAltCTPL.ObservedGravityType = baseGravityType;
            toAltCTPL.ObservedVolume = baseVolume;
            if (this.#m_ObsGravityType == VpsGravityType.Density) {
                toAltCTPL.ObservedTemperature = T15C;
                toAltCTPL.ObservedTemperatureUnit = VpsTemperatureUnit.Celcius;
                toAltCTPL.BaseTemperature = T15C;
                toAltCTPL.BaseTemperatureUnit = VpsTemperatureUnit.Celcius;
            }
            else {
                toAltCTPL.ObservedTemperature = T60F;
                toAltCTPL.ObservedTemperatureUnit = VpsTemperatureUnit.Fahrenheit;
                toAltCTPL.BaseTemperature = T60F;
                toAltCTPL.BaseTemperatureUnit = VpsTemperatureUnit.Fahrenheit;
            }

            //Convert to requested Temperature and Pressure
            toAltCTPL.AlternateTemperature = this.#m_AltQtyTemperature;
            toAltCTPL.AlternateTemperatureUnit = this.#m_AltQtyTemperatureUnit;
            toAltCTPL.AlternatePressure = this.#m_AltQtyPressure;
            toAltCTPL.AlternatePressureUnit = this.#m_AltQtyPressureUnit;
            toAltCTPL.CalculateCTPL();
            if (toAltCTPL.Exception) {
                this.RaiseError_PropNme(methodName, toAltCTPL.ExceptionDescription);
                return false;
            }
            else {
                this.#m_CTPL_BaseToAlt = RoundValueASTM(1 / parseFloat(toAltCTPL.CTPL_BaseToAlt), 0.00001);
                if (!toAltCTPL.AlternateVolume == "")
                    this.#m_AltVolume = parseFloat(toAltCTPL.AlternateVolume);

                this.#m_AltGravity = RoundValueASTM(parseFloat(toAltCTPL.GetAlternateGravity(baseGravityType)), 0.1);
                return true;
            }

        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }

    }

    ConvertVolumeAtSameTemp(volume, volumeUnit, targetUnit) {

        const methodName = "Convert Volume at same Temperature Method";
        const M3_barrel = 6.28981;
        const barrel_USG = 42.0;

        try {
            //let number = parseFloat(volume);
            let result = {};
            if (MathFormula.TryParseFloat(volume, result)) {
                switch (targetUnit) {
                    case VpsQuantityUnit.CubicMetres: {
                        switch (volumeUnit) {
                            case VpsQuantityUnit.CubicMetres:
                                return result.value;
                            case VpsQuantityUnit.Barrels:
                                return (result.value / M3_barrel);
                            case VpsQuantityUnit.USGallons:
                                return ((result.value / barrel_USG) / M3_barrel);
                        }
                    }
                        break;

                    case VpsQuantityUnit.Barrels: {
                        switch (volumeUnit) {
                            case VpsQuantityUnit.CubicMetres:
                                return (result.value * M3_barrel);
                            case VpsQuantityUnit.Barrels:
                                return result.value;
                            case VpsQuantityUnit.USGallons:
                                return (result.value / barrel_USG);
                        }
                    }
                        break;

                    case VpsQuantityUnit.USGallons: {
                        switch (volumeUnit) {
                            case VpsQuantityUnit.CubicMetres:
                                return (result.value * M3_barrel * barrel_USG);
                            case VpsQuantityUnit.Barrels:
                                return (result.value * barrel_USG);
                            case VpsQuantityUnit.USGallons:
                                return result.value;
                        }
                    }
                        break;
                }
            }

            return 0.0;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message)
            return 0.0;
        }
    }

    WeightToVolume() {

        const methodName = "Convert Weight to Volume";

        try {
            if (Approximates(this.#m_ObsGravityPress) && Approximates(this.#m_AltQtyPressure))
                this.#m_ObsGravityPressUnit = this.#m_AltQtyPressureUnit;

            if (!this.GetDensity15C())
                return false;
            else if (this.#m_AltQuantityUnit != VpsQuantityUnit.CubicMetres)
                this.#m_Density15C = RoundValueASTM(this.#m_Density15C, 0.1);

            this.#m_WCF = (this.#m_Density15C - 1.1).toString();
            this.#m_BaseVolume = (parseFloat(this.#m_ObsQuantityVl) * 1000) / parseFloat(this.#m_WCF);
            this.#m_Mass = ((this.#m_BaseVolume * this.#m_Density15C) / 1000).toString();

            if (!this.GetAlternateValues(this.#m_Density15C.toString(), VpsGravityType.Density, this.#m_BaseVolume.toString()))
                return false;

            this.#m_VCF = RoundValueASTM(this.#m_CTPL_ObsToBase * this.#m_CTPL_BaseToAlt, 0.00001).toString();

            this.#m_M3 = this.#m_AltVolume.toString();
            this.#m_BBL = this.ConvertVolumeAtSameTemp(this.#m_AltVolume.toString(), VpsQuantityUnit.CubicMetres, VpsQuantityUnit.Barrels).toString();

            return true;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message)
            return false;
        }
    }

    // Todo: Need to check. CTPL flow
    GetBaseValues(gravityValue, gravityType,
        gravityTemp, gravityTempUnit,
        gravityPress, gravityPressUnit) {
      
        const methodName = "Get Base Values Method";

        const toBaseCTPL = new CTPL();
        try {
            let alternateConditions = false;

            //Ensure Pressure Units are equal if pressures are 0. This is important in order to determine
            //whether ObsQtyVolume is an Observed Volume or an Alternate Volume.
            if (Approximates(parseFloat(gravityPress), 0.0) && Approximates(parseFloat(this.#m_ObsQtyPressure), 0.0))
                gravityPressUnit = this.#m_ObsQtyPressureUnit;

            toBaseCTPL.Commodity = this.#m_Commodity;
            toBaseCTPL.ObservedGravityValue = gravityValue;
            toBaseCTPL.ObservedGravityType = gravityType;
            toBaseCTPL.ObservedTemperature = gravityTemp;
            toBaseCTPL.ObservedTemperatureUnit = gravityTempUnit;
            toBaseCTPL.ObservedPressure = gravityPress;
            toBaseCTPL.ObservedPressureUnit = gravityPressUnit;
            if (gravityType == VpsGravityType.Density) {
                toBaseCTPL.BaseTemperature = T15C;
                toBaseCTPL.BaseTemperatureUnit = VpsTemperatureUnit.Celcius;
            }
            else {
                toBaseCTPL.BaseTemperature = T60F;
                toBaseCTPL.BaseTemperatureUnit = VpsTemperatureUnit.Fahrenheit;
            }

            toBaseCTPL.BasePressure = "0";
            toBaseCTPL.BasePressureUnit = this.#m_ObsGravityPressUnit;

            if (Approximates(parseFloat(gravityTemp), parseFloat(this.#m_ObsQtyTemperature)) &&
                gravityTempUnit == this.#m_ObsQtyTemperatureUnit &&
                gravityPressUnit == this.#m_ObsQtyPressureUnit)
                toBaseCTPL.ObservedVolume = this.#m_ObsQuantityVl;
            else {
                alternateConditions = true;
                toBaseCTPL.AlternateVolume = this.#m_ObsQuantityVl;
                toBaseCTPL.AlternateTemperature = this.#m_ObsQtyTemperature;
                toBaseCTPL.AlternateTemperatureUnit = this.#m_ObsQtyTemperatureUnit;
                toBaseCTPL.AlternatePressure = this.#m_ObsQtyPressure;
                toBaseCTPL.AlternatePressureUnit = this.#m_ObsQtyPressureUnit;
            }


            toBaseCTPL.CalculateCTPL();
            if (toBaseCTPL.Exception) {
                this.RaiseError_PropNme(methodName, toBaseCTPL.ExceptionDescription);
                return false;
            }
            else {
                if (alternateConditions)
                    this.#m_CTPL_ObsToBase = RoundValueASTM(parseFloat(toBaseCTPL.CTPL_BaseToAlt), 0.00001);
                else
                    this.#m_CTPL_ObsToBase = RoundValueASTM(parseFloat(toBaseCTPL.CTPL_ObsToBase), 0.00001);

                if (!(toBaseCTPL.BaseVolume == ""))
                    this.#m_BaseVolume = parseFloat(toBaseCTPL.BaseVolume);

                this.#m_BaseGravity = parseFloat(toBaseCTPL.GetBaseGravity(gravityType));
                this.#m_BaseDensity = parseFloat(toBaseCTPL.GetBaseGravity(VpsGravityType.Density));
                return true;
            }

        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }

    }

    VolumeToWeight() {
        const methodName = "Convert Volume to Weight";
        try {
            if (Approximates(this.#m_ObsGravityPress,0) && Approximates(this.#m_ObsQtyPressure,0))
                this.#m_ObsGravityPressUnit = this.#m_ObsQtyPressureUnit;

            if (!this.GetDensity15C())
                return false;
            else if (this.#m_ObsQuantityUnit != VpsQuantityUnit.CubicMetres)
                this.#m_Density15C = RoundValueASTM(this.#m_Density15C, 0.1);

            if (!this.GetBaseValues(this.#m_Density15C.toString(), VpsGravityType.Density, T15C, VpsTemperatureUnit.Celcius, "0", VpsPressureUnit.kPa))
                return false;

            this.#m_VCF = RoundValueASTM(this.#m_CTPL_ObsToBase, 0.00001).toString();

            let baseM3 = this.ConvertVolumeAtSameTemp(this.#m_BaseVolume.toString(), this.#m_ObsQuantityUnit, VpsQuantityUnit.CubicMetres);
            this.#m_WCF = (this.#m_BaseDensity - 1.1).toString();
            this.#m_Mass = ((this.#m_BaseDensity * baseM3) / 1000).toString();
            this.#m_MT = ((this.#m_WCF * baseM3) / 1000).toString();

            return true;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }

    VolumeToVolume() {
        const methodName = "Convert Volume to Volume";
         //;
        try {
            if (!this.GetBaseValues(this.#m_ObsGravityVl, this.#m_ObsGravityType, this.#m_ObsGravityTemp, this.#m_ObsGravityTempUnit, this.#m_ObsGravityPress, this.#m_ObsGravityPressUnit))
                return false;

            if (!this.GetAlternateValues(this.#m_BaseGravity.toString(), this.#m_ObsGravityType, this.#m_BaseVolume.toString()))
                return false;

            this.#m_VCF = RoundValueASTM(this.#m_CTPL_ObsToBase * this.#m_CTPL_BaseToAlt, 0.00001).toString();
            if (this.#m_AltQuantityUnit == VpsQuantityUnit.CubicMetres)
                this.#m_M3 = this.ConvertVolumeAtSameTemp(this.#m_AltVolume.toString(), this.#m_ObsQuantityUnit, VpsQuantityUnit.CubicMetres).toString();
            else
                this.#m_BBL = this.ConvertVolumeAtSameTemp(this.#m_AltVolume.toString(), this.#m_ObsQuantityUnit, VpsQuantityUnit.Barrels).toString();
            return true;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message)
            return false;
        }
    }
   // ValidateBoundary(0/*grvity*/,Commodity,1/*density*/,698)
    ValidateBoundary(pType,Commodity,parameterUnit,parameterValue){
       //
        let minValue;
        let maxValue; 
        let check ={
            Valid:true,
            Message:""
        }; 
        let check1 ={
            Valid:true,
            Message:""
        };
        let check2 ={
            Valid:true,
            Message:""
        };
        let check3 ={
            Valid:true,
            Message:""
        };
        let check4 ={
            Valid:true,
            Message:""
        };
        let check5 ={
            Valid:true,
            Message:""
        };
        let check6 ={
            Valid:true,
            Message:""
        };
        let check7 ={
            Valid:true,
            Message:""
        };
        let check8 ={
            Valid:true,
            Message:""
        };
      let parameterName ="";    
      let unitErrorMessage = "Invalid Parameter Unit.";
        console.log(ParameterType)
switch (pType) {
  
  case ParameterType.Gravity: {
      let unit =parameterUnit
      if(unit==""){
      check.Message = unitErrorMessage;
      return check;
     }
     if(Commodity == VpsCommodity.LubricatingOil){
      switch(unit){
          case VpsGravityType.Density:{
              parameterName = "Density" ; minValue =801.3 ; maxValue = 1163.0;
              break
          }
          case VpsGravityType.API:{
              parameterName ="API Gravity"; minValue = -10 ; maxValue = 45;
              break
          }
          case VpsGravityType.SG:{
              parameterName = "Specific Gravity" ; minValue = 0.8017 ; maxValue = 1.164;
              break
          }
          
      }
     }
     else{
      switch(unit){
          case VpsGravityType.Density:{
              parameterName = "Density" ; minValue =611.5 ; maxValue = 1163.0;
              break
          }
          case VpsGravityType.API:{
              parameterName = "API Gravity"; minValue = -10 ; maxValue = 100;
              break
            }
          case VpsGravityType.SG:{
              parameterName = "Specific Gravity" ; minValue = 0.6115 ; maxValue = 1.164;
              break
            }
           
      }
      
     }
    break;     
  }
 
  case ParameterType.Temperature: {
      let unit =parameterUnit
      if(unit==""){
      check.Message = unitErrorMessage;
      return check;
     }
     //
     if(unit == VpsTemperatureUnit.Celcius){
      parameterName = "Temperature in °C"; minValue = -50.0 ; maxValue = 150.0;
      break
     }
     else if(unit == VpsTemperatureUnit.Fahrenheit){
      parameterName = "Temperature in °F"; minValue = -58.0 ; maxValue = 302.0;
      break
     }
    break; 
  }
  case ParameterType.Pressure: {
      let unit =parameterUnit
      if(unit==""){
      check.Message = unitErrorMessage;
      return check;
     }
     if(unit == VpsPressureUnit.Psi){
      parameterName = "Pressure in Psi"; minValue = 0 ; maxValue = 1500;
      break
     }
     else if(unit == VpsPressureUnit.kPa){
      parameterName = "Pressure in kPa"; minValue = 0 ; maxValue = 10342;
      break
     }
     else{
      parameterName = "Pressure in Bar" ; minValue = 0 ; maxValue = 103.4;
      break
     }
    break;
  }
 
    
}if(check.Valid){
check.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
if(!check.Valid){
  check.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
}

}
else if (check1.Valid){
    check1.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check1.Valid){
      check1.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}
else if (check2.Valid){
    check2.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check2.Valid){
      check2.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}else if (check3.Valid){
    check3.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check3.Valid){
      check3.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}else if (check4.Valid){
    check4.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check4.Valid){
      check4.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}else if (check5.Valid){
    check5.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check5.Valid){
      check5.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}else if (check6.Valid){
    check6.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check6.Valid){
      check6.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}
else if (check7.Valid){
    check7.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check7.Valid){
      check7.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}
else if (check8.Valid){
    check8.Valid = VerifyNumericLimit_Btw_MinMax(parameterValue, minValue, maxValue)
    if(!check8.Valid){
      check8.Message = `${parameterName} must have a value between ${minValue} and ${maxValue}`
    }
}
return check;
}
    ConvertQuantity() {
        ////
        let methodName = "Convert Quantity Method";

//
        try {

            this.#m_Density15C = 0;
            this.#m_M3 = "";
            this.#m_MT = "";
            this.#m_WCF = "";
            this.#m_Mass = "";
            this.#m_BBL = "";
            this.#m_VCF = "";

            if (!this.MinimumDetails())
                return false;
            if (this.#m_ObsQuantityUnit == VpsQuantityUnit.MetricTons && this.#m_AltQuantityUnit != VpsQuantityUnit.MetricTons) {
                if (!this.WeightToVolume()) {
                    return false;
                }
            }
            else if (this.#m_ObsQuantityUnit != VpsQuantityUnit.MetricTons && this.#m_AltQuantityUnit == VpsQuantityUnit.MetricTons) {
                if (!this.VolumeToWeight()) {
                    return false;
                }
            }
            else if (this.#m_ObsQuantityUnit != VpsQuantityUnit.MetricTons && this.#m_AltQuantityUnit != VpsQuantityUnit.MetricTons) {
                if (!this.VolumeToVolume()) {
                    return false;
                }
            }
            else {
                if (!this.GetDensity15C()) {
                    return false
                }
                else {
                    this.#m_MT = this.#m_ObsQuantityVl
                    this.#m_WCF = (this.#m_Density15C - 1.1).toString()
                    this.#m_BaseVolume = (parseFloat(this.#m_ObsQuantityVl) * 1000) / parseFloat(this.#m_WCF)
                    this.#m_Mass = ((this.#m_BaseVolume * this.#m_Density15C) / 1000).toString()
                }
            }
            return true
        }
        catch (ex) {
            alert(methodName, ex.Message)
            return false;
        }
    }
}